import { SIGNAL_LINK_RANGE_SQ } from '../../data/signalLinks.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { ControlNode } from '../entities/ControlNode.js';
import type { EntityId, PlayerId } from '../ids.js';

// V1.5 Signal Link layer per docs/02_GAME_DESIGN.md §11 Option A.
//
// Each tick (when state.enableSignalLinks is true), recompute every owned
// relay's `isConnected` via BFS rooted at the player's Core. A relay is
// connected if there's a path of owned relays from the relay back to the
// Core where each adjacent pair is within SIGNAL_LINK_RANGE_PX of each other
// AND the Core itself is within range of the first hop.
//
// Effect plumbing lives in the system that owns the rule (Rule 12):
//   - EconomySystem reads `node.isConnected` to scale relay income.
//   - FogSystem reads `node.isConnected` to skip vision stamping for isolated
//     relays.
//   - ForwardTurretNetworkSystem reads `node.isConnected` to anchor `mini_turret`
//     buildings.
// SignalLinkSystem does not touch income, vision, turrets, or any other
// gameplay number; it only writes the boolean.
//
// V1.5+ Slice 2 — Cut Link integration. A relay with `state.tick <
// linkCutUntilTick` is treated as a non-conductor: it is forced
// `isConnected: false` (so its own income/vision drops) AND BFS will not
// traverse THROUGH it (so downstream owned relays drop to isolated and any
// `mini_turret` anchored on them goes offline via Slice 1's pipeline). This
// is the §15 "anti-deathball" lever — a single Cut Link can cascade through
// a player's chain to disable forward firepower.
//
// Performance: relay count is small (3 in V1.5 Foundry, 5–7 expected on a
// future map). The naive O(N²) reach test per BFS step is fine.
//
// Order: this system runs after CaptureSystem (so freshly flipped relays read
// the current owner) and before EconomySystem / FogSystem (so the income and
// vision systems consume the up-to-date flag).
export function signalLinkSystem(state: MatchState): void {
  if (!state.enableSignalLinks) return;
  // Clear all relays first, then mark connected ones via two BFS passes (one
  // per player). Neutral relays end up `isConnected: false` because no Core
  // owns them — that's fine; EconomySystem doesn't read isConnected for
  // neutral relays (they have no income), and FogSystem doesn't stamp vision
  // for neutral nodes anyway (the existing ownerId === null guard).
  for (const node of Object.values(state.controlNodes)) {
    node.isConnected = false;
  }

  for (const core of Object.values(state.cores)) {
    bfsFromCore(state, core.ownerId, core.pos);
  }

  // Telemetry — after BFS settles each owned relay's isConnected flag, count
  // owned-but-isolated relays per player and accumulate seconds. Surfaces
  // "how often does Option A bite?" in the playtest summary.
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === null) continue;
    if (node.isConnected) continue;
    state.stats.isolatedRelaySecondsByPlayer[node.ownerId] += SECONDS_PER_TICK;
  }
  // V1.5+ Slice 2 — cut-active seconds. Sum across every relay currently
  // sitting in a cut window, attributed to the player who landed the cut.
  // Ignore expired cuts (state.tick already past linkCutUntilTick).
  for (const node of Object.values(state.controlNodes)) {
    if (node.linkCutByPlayerId === null) continue;
    if (state.tick >= node.linkCutUntilTick) continue;
    state.stats.cutLinkActiveSecondsByPlayer[node.linkCutByPlayerId] += SECONDS_PER_TICK;
  }
  // V1.5+ Slice 5 — jam-active seconds. Same shape as cut-active. Jammed
  // nodes still conduct (the BFS above traversed through them), so the
  // accumulation lives here purely for telemetry — there's no behavioural
  // hook in this system. FogSystem reads `jammedUntilTick` directly to
  // skip the jammed node's vision stamp.
  for (const node of Object.values(state.controlNodes)) {
    if (node.jammedByPlayerId === null) continue;
    if (state.tick >= node.jammedUntilTick) continue;
    state.stats.jamRelayActiveSecondsByPlayer[node.jammedByPlayerId] += SECONDS_PER_TICK;
  }
  // V1.5+ Slice 6 — disrupt-active seconds. Same shape as jam-active. The
  // disruption effect itself is read by CombatSystem (fire-rate / range
  // multipliers); accumulation lives here purely for telemetry alongside
  // the other disruption counters.
  for (const node of Object.values(state.controlNodes)) {
    if (node.disruptedByPlayerId === null) continue;
    if (state.tick >= node.disruptedUntilTick) continue;
    state.stats.disruptUplinkActiveSecondsByPlayer[node.disruptedByPlayerId] +=
      SECONDS_PER_TICK;
  }
  // V1.5+ Slice 7 — fortify-active seconds. Same shape as the disruption
  // counters. The fortify effect itself is read by CombatSystem (fire-rate
  // buff) and AbilitySystem (incoming cut/jam/disrupt immunity);
  // accumulation lives here purely for telemetry. The §17 gate watches
  // whether high fortify uptime correlates with falling relay-flip rate —
  // that would be the failure mode (defensive turtling-lite).
  for (const node of Object.values(state.controlNodes)) {
    if (node.fortifiedByPlayerId === null) continue;
    if (state.tick >= node.fortifiedUntilTick) continue;
    state.stats.fortifyRelayActiveSecondsByPlayer[node.fortifiedByPlayerId] +=
      SECONDS_PER_TICK;
  }
}

function isCutNode(state: MatchState, node: ControlNode): boolean {
  return state.tick < node.linkCutUntilTick;
}

function bfsFromCore(
  state: MatchState,
  ownerId: PlayerId,
  corePos: { x: number; y: number },
): void {
  const owned: ControlNode[] = [];
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === ownerId) owned.push(node);
  }
  if (owned.length === 0) return;
  // Seed the frontier with relays directly within range of the Core. These
  // are the "first hop" — connected without an intermediary. Cut nodes are
  // skipped here so they neither become connected themselves nor seed a
  // sub-chain rooted on them.
  const visited = new Set<EntityId>();
  const frontier: ControlNode[] = [];
  for (const node of owned) {
    if (isCutNode(state, node)) continue;
    if (sqDist(node.pos, corePos) <= SIGNAL_LINK_RANGE_SQ) {
      frontier.push(node);
      visited.add(node.id);
    }
  }
  // BFS through owned, non-cut relays, following the link-range adjacency.
  // A cut node is BOTH non-traversable (so downstream chains break) AND not
  // marked connected (so it pays the isolated-income penalty itself).
  while (frontier.length > 0) {
    const current = frontier.shift()!;
    current.isConnected = true;
    for (const candidate of owned) {
      if (visited.has(candidate.id)) continue;
      if (isCutNode(state, candidate)) continue;
      if (sqDist(current.pos, candidate.pos) > SIGNAL_LINK_RANGE_SQ) continue;
      visited.add(candidate.id);
      frontier.push(candidate);
    }
  }
}

function sqDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
